import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { getTrackReport } from '@/store/tracks/actions';

import { selectChat } from '@/store/chat/selectors';
import { selectTracks } from '@/store/tracks/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Alert, Button, Col, Flex, message, Row, Skeleton, Tag, Typography } from 'antd';
import TrackProgress from '@/components/TrackProgress';
import { InfoCircleFilled } from '@ant-design/icons';

import { FINISHED } from '../../constants';
import TrackRewards from './TrackRewards';

import useIsMobile from '@shared/hooks/useIsMobile';
import { fileExport } from '@shared/utils/file-export';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';

import css from './TrackDescription.module.scss';

const { Title, Text } = Typography;

const TrackDescriptionSkeleton = ({ loading, hasContent, children, small }) => {
  switch (loading) {
    case true:
      return <Skeleton active paragraph={{ rows: small ? 1 : 2 }} />;

    default:
      return hasContent ? children : <></>;
  }
};

const TrackDescription = () => {
  const { t } = useTranslation('TrackId');

  const dispatch = useDispatch();
  const {
    track,
    isLoading,
    trackSections,
    partialFinishing,
    trackStudentPlanStatus,
    tags,
    isTrackReportLoading,
    hasRewards,
    sectionsRewards,
  } = useSelector(selectTracks);
  const { trackChatId } = useSelector(selectChat);
  const { currentUser } = useSelector(selectUsers);

  const navigate = useNavigate();
  const [isMobile] = useIsMobile(769);

  const handleFileReport = () => {
    dispatch(
      getTrackReport(
        track?.id,
        blob => {
          const ext = blob?.type ? blob.type.split('/')[1] : '';
          const fileName = `${track.name}_${dayjs().format('DD.MM.YYYY/HH:mm')}${ext ? '.' + ext : ''}`;

          fileExport(blob, fileName, true);
        },
        () => message.error(t('exportError'))
      )
    );
  };

  const isTrackPlanStatusFinished = trackStudentPlanStatus && trackStudentPlanStatus === FINISHED;

  const renderTags = useCallback(
    key =>
      Array.isArray(track?.[key]) &&
      track?.[key]?.length !== 0 &&
      track?.[key].map(tag => (
        <Tag key={tag.id} className={css['TrackDescription-tag']} color='var(--personalColor)'>
          {tag?.name}
        </Tag>
      )),
    [track]
  );

  const trackProgressBlock = (
    <>
      <Title className={css['TrackDescription-title']} level={3}>
        {t('progress')}
      </Title>
      <TrackProgress finishedTopics={track?.finishedTopics} totalTopics={track?.totalTopics} loading={isLoading} />
      {partialFinishing > 0 && (
        <Alert
          message={
            <span>
              {t('trackCompletionInfo', {
                count: partialFinishing,
                countSections: trackSections.length,
              })}
            </span>
          }
          type='info'
          showIcon
          icon={<InfoCircleFilled className={css['TrackDescription-section-progress-info']} />}
        />
      )}
      {isTrackPlanStatusFinished && (
        <Button type='primary' onClick={handleFileReport} loading={isTrackReportLoading}>
          {t('exportTrackStat')}
        </Button>
      )}
    </>
  );

  return (
    <Flex vertical>
      <Row gutter={{ lg: 40, sm: 20 }}>
        <Col lg={16} sm={24}>
          <Flex vertical gap={32}>
            {/* Подробное описание */}
            {isMobile && trackProgressBlock}
            <Flex gap={16} vertical>
              <TrackDescriptionSkeleton loading={isLoading} hasContent={!!track?.fullDescription}>
                <Title className={css['TrackDescription-title']} level={3}>
                  {t('detailedContent')}
                </Title>
                <Text className={css['TrackDescription-text']} data-qa='trackFullDescription'>
                  {track?.fullDescription}
                </Text>
              </TrackDescriptionSkeleton>
            </Flex>

            {/* Область развития */}
            <Flex gap={16} vertical>
              <TrackDescriptionSkeleton loading={isLoading} hasContent={!isEmpty(track?.categories)} small>
                <Title className={css['TrackDescription-title']} level={3}>
                  {t('category')}
                </Title>
                <Flex wrap gap={12} data-qa='trackCategoryList'>
                  {renderTags('categories')}
                </Flex>
              </TrackDescriptionSkeleton>
            </Flex>

            {/* Компетенции */}
            <Flex gap={16} vertical>
              <TrackDescriptionSkeleton loading={isLoading} hasContent={!isEmpty(track?.competences)} small>
                <Title className={css['TrackDescription-title']} level={3}>
                  {t('competences')}
                </Title>
                <Flex wrap gap={12} data-qa='trackCompetencesList'>
                  {renderTags('competences')}
                </Flex>
              </TrackDescriptionSkeleton>
            </Flex>

            {/* Навыки */}
            <Flex gap={16} vertical>
              <TrackDescriptionSkeleton loading={isLoading} hasContent={!isEmpty(track?.skills)} small>
                <Title className={css['TrackDescription-title']} level={3}>
                  {t('skills')}
                </Title>
                <Flex wrap gap={12} data-qa='trackSkillsList'>
                  {renderTags('skills')}
                </Flex>
              </TrackDescriptionSkeleton>
            </Flex>

            {/* Достижения */}
            {(hasRewards || sectionsRewards?.length > 0) && <TrackRewards />}
          </Flex>
        </Col>

        <Col lg={8} sm={24} className={css['TrackDescription-section-progress']}>
          <Flex vertical gap={16}>
            {/* Прогресс */}
            {trackStudentPlanStatus && (
              <>
                {!!trackChatId && currentUser?.domainCompany?.hasChat && track?.withChat && (
                  <Flex justify='flex-end'>
                    <Button type='primary' onClick={() => navigate(`/chat/group?chatId=${trackChatId}`)}>
                      {t('goToChat')}
                    </Button>
                  </Flex>
                )}

                {!isMobile && trackProgressBlock}
              </>
            )}

            {/* Теги */}
            {tags?.length > 0 && (
              <>
                <Title
                  className={classNames(css['TrackDescription-title-tags'], {
                    [css['TrackDescription-title-tags-single']]: !trackStudentPlanStatus,
                  })}
                  level={3}
                >
                  {t('tags')}
                </Title>
                <div className={css['TrackDescription-tags']}>
                  {tags.map(tag => (
                    <div key={tag.id}>
                      <Link to={`/collection-tracks?tagIds=${tag.id}`} state={{ tagName: tag.name }}>
                        {tag.name}
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Flex>
        </Col>
      </Row>
    </Flex>
  );
};

export default TrackDescription;
